import * as monacoEditor from 'monaco-editor';
import { useCallback } from 'react';

import {
  boldSelection,
  italicizeSelection,
  linkSelection,
} from 'src/hooks/useKeypressListener/hotkeyDictionary';
import { useKeypressListener } from 'src/hooks/useKeypressListener/useKeypressListener';

/**
 * This hook returns functions to transform current editor selection with common markdown formatting.
 * It will also setup the keyboard shortcuts for these actions.
 */
export function useMarkdownTransforms({
  editor,
}: {
  editor: monacoEditor.editor.IStandaloneCodeEditor;
}) {
  const getCurrentTarget = useCallback(() => {
    const selection = editor.getSelection();
    if (selection) return selection;

    const position = editor.getPosition();
    if (position) {
      return monacoEditor.Range.fromPositions(position, position);
    }
  }, [editor]);
  const transformSelection = useCallback(
    ({
      replacement,
      endSelectionOffset,
      endSelectionLength,
    }: {
      replacement: string;
      endSelectionOffset: number;
      endSelectionLength: number;
    }) => {
      const selection = editor.getSelection();

      if (selection) {
        const edit: monacoEditor.editor.IIdentifiedSingleEditOperation = {
          range: selection,
          text: replacement,
          forceMoveMarkers: false,
        };
        editor.executeEdits(
          'keypress',
          [edit],
          [
            monacoEditor.Selection.fromPositions(
              selection.getStartPosition().delta(0, endSelectionOffset),
              selection
                .getStartPosition()
                .delta(0, endSelectionOffset + endSelectionLength),
            ),
          ],
        );
      }
    },
    [editor],
  );

  /**
   * @returns
   * `selectionValue`: the string currently selected
   * `selectionWithSurroundingText`: the monaco Selection for the current selection
   *     + the space where start text and end text would be
   * `selectionAlreadyWrappedWithText`: a boolean if the current selection is already wrapped with
   *    the start text and end text
   */
  const getSelectionContext = useCallback(
    ({
      selection,
      startText,
      endText,
    }: {
      selection: monacoEditor.Selection | monacoEditor.Range;
      startText: string;
      endText: string;
    }) => {
      const selectionWithSurroundingText = monacoEditor.Selection.fromPositions(
        selection.getStartPosition().delta(0, -startText.length),
        selection.getEndPosition().delta(0, endText.length),
      );

      const selectionWithSurroundingTextValue = editor
        .getModel()
        ?.getValueInRange(selectionWithSurroundingText);

      const selectionAlreadyWrappedWithText = !!(
        selectionWithSurroundingTextValue?.startsWith(startText) &&
        selectionWithSurroundingTextValue.endsWith(endText)
      );

      return {
        selectionWithSurroundingText,
        selectionAlreadyWrappedWithText,
      };
    },
    [editor],
  );

  const makeSelectionBold = useCallback(() => {
    const startText = '**';
    const endText = '**';

    const selection = getCurrentTarget();

    if (!selection) return;

    const selectionValue = editor.getModel()?.getValueInRange(selection);

    if (typeof selectionValue !== 'string') return;

    const { selectionWithSurroundingText, selectionAlreadyWrappedWithText } =
      getSelectionContext({
        selection,
        startText,
        endText,
      });
    // if the selection already has ** ** wrapping it, we want
    // to remove these chars
    if (selectionAlreadyWrappedWithText) {
      // set the selection to the whole section that will be replaces
      editor.setSelection(selectionWithSurroundingText);
      transformSelection({
        replacement: selectionValue,
        endSelectionOffset: 0,
        endSelectionLength: selectionValue.length,
      }); // otherwise, add ** **
    } else {
      transformSelection({
        replacement: `${startText}${selectionValue}${endText}`,
        endSelectionOffset: startText.length,
        endSelectionLength: selectionValue.length,
      });
    }
  }, [editor, getCurrentTarget, getSelectionContext, transformSelection]);
  const makeSelectionItalic = useCallback(() => {
    const startText = '_';
    const endText = '_';
    const selection = getCurrentTarget();
    if (!selection) return;

    const selectionValue = editor.getModel()?.getValueInRange(selection);

    if (typeof selectionValue !== 'string') return;
    const { selectionWithSurroundingText, selectionAlreadyWrappedWithText } =
      getSelectionContext({
        selection,
        startText,
        endText,
      });
    // if the selection already has _ _ wrapping it, we want
    // to remove these chars
    if (selectionAlreadyWrappedWithText) {
      // set the selection to the whole section that will be replaces
      editor.setSelection(selectionWithSurroundingText);
      transformSelection({
        replacement: selectionValue,
        endSelectionOffset: 0,
        endSelectionLength: selectionValue.length,
      }); // otherwise, add _ _
    } else {
      transformSelection({
        replacement: `${startText}${selectionValue}${endText}`,
        endSelectionOffset: startText.length,
        endSelectionLength: selectionValue.length,
      });
    }
  }, [editor, getCurrentTarget, getSelectionContext, transformSelection]);
  const makeSelectionLink = useCallback(() => {
    const startText = '[';
    const endText = ']()';
    const selection = getCurrentTarget();
    if (!selection) return;

    const selectionValue = editor.getModel()?.getValueInRange(selection);

    if (typeof selectionValue !== 'string') return;

    transformSelection({
      replacement: `${startText}${selectionValue}${endText}`,
      // put the cursor in the spot where you can put a link
      endSelectionOffset:
        selectionValue.length + startText.length + endText.length - 1,
      endSelectionLength: 0,
    });
  }, [editor, transformSelection, getCurrentTarget]);
  const makeSelectionQuote = useCallback(() => {
    const startText = '> ';
    const endText = '';

    const selection = getCurrentTarget();
    if (!selection) return;
    const startPosition = selection.getStartPosition();
    const fullSelection = selection.setStartPosition(
      startPosition.lineNumber,
      1,
    );

    const selectionValue = editor.getModel()?.getValueInRange(fullSelection);
    if (typeof selectionValue !== 'string') return;

    const { selectionWithSurroundingText, selectionAlreadyWrappedWithText } =
      getSelectionContext({ selection: fullSelection, startText, endText });

    if (selectionAlreadyWrappedWithText) {
      // set the selection to the whole section that will be replaces
      editor.setSelection(selectionWithSurroundingText);
      transformSelection({
        replacement: selectionValue.replace(/^> /, ''),
        endSelectionOffset: 0,
        endSelectionLength: selectionValue.length - 2,
      });
    } else {
      editor.setSelection(fullSelection);
      transformSelection({
        replacement: `${startText}${selectionValue}${endText}`,
        endSelectionOffset: startText.length,
        endSelectionLength: selectionValue.length,
      });
    }
  }, [editor, getCurrentTarget, getSelectionContext, transformSelection]);
  const makeSelectionCode = useCallback(() => {
    const startText = '```\n';
    const endText = '\n```';

    const selection = getCurrentTarget();
    if (!selection) return;

    const selectionValue = editor.getModel()?.getValueInRange(selection);
    if (typeof selectionValue !== 'string') return;

    const { selectionWithSurroundingText, selectionAlreadyWrappedWithText } =
      getSelectionContext({ selection, startText, endText });

    if (selectionAlreadyWrappedWithText) {
      // set the selection to the whole section that will be replaces
      editor.setSelection(selectionWithSurroundingText);
      transformSelection({
        replacement: selectionValue,
        endSelectionOffset: 0,
        endSelectionLength: selectionValue.length,
      });
    } else {
      transformSelection({
        replacement: `${startText}${selectionValue}${endText}`,
        endSelectionOffset: startText.length,
        endSelectionLength: selectionValue.length,
      });
    }
  }, [editor, getCurrentTarget, getSelectionContext, transformSelection]);

  useKeypressListener(italicizeSelection, makeSelectionItalic);
  useKeypressListener(linkSelection, makeSelectionLink);
  useKeypressListener(boldSelection, makeSelectionBold);

  return {
    makeSelectionBold,
    makeSelectionItalic,
    makeSelectionLink,
    makeSelectionQuote,
    makeSelectionCode,
  };
}
